"use client";

import React from "react";
import { ArrowUpRight, Activity, BookOpen, Briefcase, Truck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const industries = [{
  id: "real-estate",
  title: "Real Estate & Developers",
  description: "Transform pre-construction sales with hyper-realistic 3D walkthroughs and immersive digital twins.",
  statistic: "40% Higher Sales Conversion",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1621178/img/real_estate_showcase.png",
  icon: Activity,
  className: "md:col-span-2 md:row-span-2 min-h-[400px] md:min-h-[500px]",
  isLarge: true
}, {
  id: "interior-design",
  title: "Interior Design",
  description: "Interactive staging tools that allow clients to visualize furniture and finishes in real-time.",
  statistic: "3x Client Engagement",
  icon: BookOpen,
  className: "md:col-span-1 md:row-span-1 min-h-[250px]",
  isLarge: false
}, {
  id: "hospitality",
  title: "Hospitality",
  description: "Immersive virtual tours for luxury hotels and resorts designed to drive direct bookings.",
  statistic: "25% Increase in Bookings",
  icon: Briefcase,
  className: "md:col-span-1 md:row-span-1 min-h-[250px]",
  isLarge: false
}, {
  id: "urban-planning",
  title: "Urban Planning",
  description: "Scale model visualization for smart cities and large-scale infrastructure projects.",
  statistic: "100+ Cities Digitized",
  icon: Truck,
  className: "md:col-span-2 lg:col-span-3 md:row-span-1 min-h-[250px]",
  isLarge: false
}];
export default function SectorExpertise() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="108705" id="industry-showcase-transformation" className="dark bg-background text-foreground py-24 px-6 md:px-12 lg:px-24 overflow-hidden relative">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/10 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div ref={headerRef} className={`mb-16 max-w-2xl transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <h2 className="text-4xl md:text-5xl font-default font-bold tracking-tighter mb-6 text-foreground">
            Transform how Industries <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/60">
              Showcase Properties
            </span>
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed font-default">
            PropVR.ai delivers immersive spatial intelligence and high-fidelity 3D visualization tailored to the unique demands of your industry. Precision, immersion, and engagement.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 transition-all duration-1000 delay-200 ease-out ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {industries.map((industry, i) => {
          const Icon = industry.icon;
          return <Card key={industry.id} data-index={i} className={`group relative overflow-hidden p-0 border-border/20 bg-card/40 backdrop-blur-xl shadow-2xl transition-all duration-500 hover:border-primary/50 rounded-xl ${industry.className}`}>
                {}
                {industry.image && <div className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 group-hover:scale-105" style={{
              backgroundImage: `url(${industry.image})`
            }}>
                    <div className="absolute inset-0 bg-background/90 mix-blend-multiply" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />
                  </div>}

                {}
                <div className="relative z-10 h-full flex flex-col justify-between p-8 md:p-10">
                  <div className="flex justify-between items-start">
                    <div className="p-3 rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20 mb-6">
                      <Icon className="w-6 h-6" />
                    </div>
                    <ArrowUpRight className="w-6 h-6 text-muted-foreground opacity-0 -translate-x-4 translate-y-4 transition-all duration-500 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0" />
                  </div>

                  <div className="mt-auto">
                    <h3 className={`font-default font-bold tracking-tight text-foreground mb-3 ${industry.isLarge ? "text-3xl md:text-4xl" : "text-2xl"}`}>
                      {industry.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed max-w-md font-default">
                      {industry.description}
                    </p>

                    {}
                    <div className="mt-8 overflow-hidden h-0 group-hover:h-auto transition-all duration-500 ease-in-out">
                      <div className="flex items-center gap-4 transform translate-y-8 opacity-0 transition-all duration-500 delay-100 ease-out group-hover:translate-y-0 group-hover:opacity-100 pt-4 border-t border-border/50">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                        <span className="text-primary font-mono font-semibold tracking-wider uppercase text-sm md:text-base">
                          {industry.statistic}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}